import { useCallback, useEffect, useState } from 'react';

import { findAllowedURLParams, findNextPath } from '../utils';
import { useCurrentPath } from './useCurrentPath';
import { useFlow } from './useFlow';
import { useFlowMetadata } from './useFlowMetadata';
import { useFlowState } from './useFlowState';
import { useGoToStep } from './useGoToStep';
import { useRootPath } from './useRootPath';

export type GoToNextStepOptions = {
  replace?: boolean;
  stopAtPath?: string;
};

export function useGoToNextStep(): [
  { error?: Error; loading: boolean },
  (options?: GoToNextStepOptions) => void
] {
  const currentPath = useCurrentPath();
  const { value: rootPath } = useRootPath();
  const [initiated, setInitiated] = useState(false);
  const [options, setOptions] = useState<GoToNextStepOptions>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error>();
  const { value: state } = useFlowState();
  const { value: flow } = useFlow();
  const { goToStep } = useGoToStep();
  const { setValue: setFlowMetadata } = useFlowMetadata();
  const urlParams = findAllowedURLParams();

  const findAndNavigate = useCallback(
    async (options?: GoToNextStepOptions) => {
      try {
        const nextPath = await findNextPath(
          flow,
          state,
          rootPath,
          options?.stopAtPath
        );
        if (nextPath !== currentPath) {
          setFlowMetadata((prevFlowMetadata) => ({
            ...prevFlowMetadata,
            stepCount: prevFlowMetadata.stepCount + 1,
          }));
          goToStep(nextPath, {
            queryParams: urlParams,
            replace: options?.replace,
          });
        }
      } catch (e) {
        setError(e as Error);
      } finally {
        setLoading(false);
      }
    },
    [flow, state, rootPath, currentPath, setFlowMetadata, goToStep, urlParams]
  );

  const goToNextStep = useCallback(
    (options?: GoToNextStepOptions) => {
      setLoading(true);
      setOptions(options);
      setInitiated(true);
    },
    [setInitiated, setLoading]
  );

  useEffect(() => {
    if (initiated) {
      setInitiated(false);
      void findAndNavigate(options);
    }
  }, [findAndNavigate, initiated, options]);

  return [{ error, loading }, goToNextStep];
}
